import { useState, useContext, useEffect, useRef } from 'react';
import { formatCurrency as currency } from '../../utils/formatCurrency';
import { getDisplayPrice, getOrigPrice } from '../../utils/amountHelper';
import { ecomAddProduct, ecomRemoveProduct } from '../../utils/ecomHelper';
import API from '../../services/apiService';
import { CartContext } from '../../store/CartContext';
import parse from 'html-react-parser';

// ESlint complains about prop-types, you need to define it
import PropTypes from 'prop-types';
EINRegistration.propTypes = {
  item: PropTypes.object.isRequired,
  prefix: PropTypes.string,
};

export default function EINRegistration({ item, prefix = '' }) {
  const [cartState, cartDispatch] = useContext(CartContext);

  const replaceUpsells = item.upSellProducts
    .filter((upsell) => upsell.upsellSectionId === 1)
    .sort((a, b) => a.upsellOrderIndex - b.upsellOrderIndex);

  // thse first item in the replace array is the EIN Application upsell
  const upsell = replaceUpsells.slice(0, 1)[0];

  // return nothing if no upsell is not found
  if (!upsell) {
    return null; // Return null if no upsell is found
  }

  const isUpsellTypeReplace = upsell.upsellType === 'Replace';

  const upsellDisplayPrice = getDisplayPrice(upsell, true);
  const origPrice = getOrigPrice(upsell, true);

  const origPriceDisplay = isUpsellTypeReplace
    ? origPrice -
      (!upsell.hiddenFee
        ? item.unitPriceWithDiscountAndFeeIfHidden + upsell.unitThirdPartyFee
        : item.unitPriceWithDiscountAndFeeIfHidden)
    : origPrice;

  const priceDisplay = isUpsellTypeReplace
    ? upsellDisplayPrice -
      (!upsell.hiddenFee
        ? item.unitPriceWithDiscountAndFeeIfHidden + upsell.unitThirdPartyFee
        : item.unitPriceWithDiscountAndFeeIfHidden)
    : upsellDisplayPrice;

  const [isChecked, setIschecked] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setIschecked(upsell.isSelected);
  }, [upsell.isSelected]);

  const storeCart = (cartData) => {
    cartDispatch({
      type: 'UPDATE',
      payload: {
        ...cartData,
        isLoading: false,
        isUpdatingCart: false,
      },
    });

    // trigger to show the toast notification
    cartDispatch({
      type: 'EXPERIENCE',
      payload: {
        showNotification: true,
      },
    });
  };

  const storeCartProducts = (updatedCartProducts) => {
    cartDispatch({
      type: 'UPDATE',
      payload: {
        cartProducts: updatedCartProducts,
        isLoading: true,
        isUpdatingCart: true,
      },
    });
  };

  const upsellDebounce = useRef();

  const handleChange = () => {
    setIsUpdating(true);

    setIschecked(!isChecked);

    // trigger to hide the toast notification
    cartDispatch({
      type: 'EXPERIENCE',
      payload: {
        showNotification: false,
      },
    });

    const updatedCartProducts = cartState?.cartProducts?.map((product) => {
      if (product.cart_ProductId === item.cart_ProductId) {
        const upsells = product.upSellProducts;
        const updatedUpsells = upsells.map((productUpsell) => {
          if (productUpsell.cart_ProductId === upsell.cart_ProductId) {
            if (productUpsell.upsellType === 'Replace') {
              productUpsell.subTotal = productUpsell.subTotal - item.unitPriceWithDiscountAndFeeIfHidden;
              productUpsell.unitPriceWithDiscountAndFee =
                productUpsell.unitPriceWithDiscountAndFee - item.unitPriceWithDiscountAndFeeIfHidden;
              productUpsell.unitPriceWithDiscountAndFeeIfHidden =
                productUpsell.unitPriceWithDiscountAndFeeIfHidden - item.unitPriceWithDiscountAndFeeIfHidden;
            }
            // console.log('Update Upsell', {
            //   ...productUpsell,
            //   isSelected: !isChecked,
            // });
            return {
              ...productUpsell,
              // isSelected: !isChecked,
              // below is a hotfix for a cart API issue, need to remove when cart API is fixed
              isSelected: productUpsell.upsellType !== 'Replace' ? !isChecked : productUpsell.isSelected,
            };
          }
          return productUpsell;
        });
        return {
          ...product,
          upSellProducts: updatedUpsells,
        };
      }
      return product;
    });

    storeCartProducts(updatedCartProducts);

    // debouncing API call in case the user repeated clicks the upsell option
    clearTimeout(upsellDebounce.current);
    upsellDebounce.current = setTimeout(() => {
      const upsellData = {
        cartProductId: upsell.cart_ProductId, // upsell cart_ProductId
        cartId: item.cartId, // cartId
        productId: upsell.productId,
        quantity: item.quantity, // use quantity of main product or you can leave 1, it will automatically use the main product's quantity
        isSelected: !isChecked, // true/false, add or remove upsell
      };

      API.updateUpsell(upsellData)
        .then((res) => {
          // console.log('cart', res);
          const cart = res.data;
          setIsUpdating(false);
          storeCart(cart);

          // push event and data to dataLayer
          if (upsellData.isSelected) {
            ecomAddProduct(upsell);
          } else {
            ecomRemoveProduct(upsell);
          }
        })
        .catch((err) => {
          console.error(err?.response?.data);

          // show error message or reload the page
          cartDispatch({
            type: 'ERROR',
            payload: {
              isLoading: false,
              isError: true,
              error: err?.response?.data || 'General Error',
            },
          });

          if (err?.response?.data?.status >= 400) {
            console.error('Upsell was not updated!');
          }
        });
    }, 300);
  };

  const lowercaseFirstLetter = (str) => {
    if (!str) return str;
    return str.charAt(0).toLowerCase() + str.slice(1);
  };

  return (
    <div className="upsell-group">
      <h3 className="fs-3 fw-500 mb-3">EIN Application</h3>
      <div className="upsell-description short-description d-block lh-15">{parse(upsell.description)}</div>
      <p className="mt-3 mb-4">
        {upsell.upsellType === 'Replace' && !upsell.isSelected && (
          <>
            We offer this as an option for only{' '}
            {upsell.discountSource && upsell.discountSource !== 'BuiltIn' && (
              <span className="orig-price me-2">{currency(origPriceDisplay)}</span>
            )}
            {currency(priceDisplay)}.
          </>
        )}
      </p>

      <div
        className={`upsell-item d-inline-block rounded border border-success bg-transparent ${
          isChecked ? 'is-selected' : ''
        } ${cartState.isLoading ? 'is-loading' : ''}`}
        data-upsell-type={upsell.upsellType}
        data-product-id={upsell.productId}
      >
        <div className="form-check">
          {isUpdating ? (
            <span className="spinner-border spinner-border-sm upsell-check" role="status" aria-hidden="true"></span>
          ) : (
            <input
              className="form-check-input"
              type="checkbox"
              value={upsell.productId}
              id={`${prefix}upsell-cart-id-${upsell.cart_ProductId}`}
              checked={isChecked}
              onChange={handleChange}
              data-cart-product-id={upsell.cart_ProductId}
              data-upsell-type={upsell.upsellType}
            />
          )}

          <div className="d-flex justify-content-between">
            <label className="form-check-label d-block" htmlFor={`${prefix}upsell-cart-id-${upsell.cart_ProductId}`}>
              <span className="fw-600 d-block d-sm-flex">
                <span className="upsell-name fw-400 d-inline-block">
                  <span className="fw-600 text-success">I need to {lowercaseFirstLetter(upsell.name)}</span>
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
